// Entry point: wires the shared modules once per page. Nothing here owns
// commerce state; pp-cart does, with Shopify as the authority.

import { createCart } from '@pp/cart';
import { evaluate, createDismissals, worldFromData } from '@pp/offers';
import { renderDelivery } from '@pp/delivery';
import { createCheckout, createDialogView } from '@pp/checkout';
import { track } from '@pp/track';
import { formatMoney } from '@pp/money';
import * as ui from '@pp/ui';
import { createCartControls, cartMessageFor } from '@pp/cart-controls';

const readJson = (el) => {
  if (!el) return null;
  try { return JSON.parse(el.textContent); } catch { return null; }
};

const config = readJson(document.getElementById('pp-config')) || {};
const strings = config.strings || {};
const money = (cents) => formatMoney(cents, config.moneyFormat);
const isCartPage = document.body.classList.contains('template-cart');
const live = document.querySelector('[data-pp-live]');

document.documentElement.classList.add('js');

function announce(text) {
  if (!live || !text) return;
  live.textContent = '';
  requestAnimationFrame(() => { live.textContent = text; });
}

// the kind of the cart message now shown ('' = none); see cartMessageFor
let cartMessageKind = '';

function showCartMessage(text, kind = 'notice') {
  for (const el of document.querySelectorAll('[data-pp-cart-message]')) {
    el.textContent = text || '';
    el.hidden = !text;
  }
  cartMessageKind = text ? kind : '';
  announce(text);
}

/* ---------------------------------------------------------------- cart */

const sectionIds = () => [
  config.sections && config.sections.miniCart,
  ...[...document.querySelectorAll('[data-pp-section]')].map((el) => el.dataset.ppSection)
];

const cart = createCart({
  fetch: (url, init) => fetch(url, init),
  routes: config.routes,
  sections: sectionIds,
  sectionsUrl: () => window.location.pathname + window.location.search
});

const cartData = () => readJson(document.querySelector('[data-pp-cart-data]')) || {};
cart.seed(cartData().cart);

/** Swap the [data-pp-replace] regions of one re-rendered section, keeping focus. */
function replaceSection(id, html) {
  const host = document.getElementById('shopify-section-' + id);
  if (!host || typeof html !== 'string') return false;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const active = document.activeElement;
  const lineEl = active && host.contains(active) ? active.closest('[data-line-key]') : null;
  const focusKey = lineEl ? lineEl.dataset.lineKey : null;
  const focusRole = active && active.matches('input') ? 'input' : active && active.dataset.qtyStep ? 'step' + active.dataset.qtyStep : active && active.dataset.lineRemove ? 'remove' : null;
  const hadFocus = active && host.contains(active);
  let swapped = false;
  for (const fresh of doc.querySelectorAll('[data-pp-replace]')) {
    const old = host.querySelector(`[data-pp-replace="${fresh.dataset.ppReplace}"]`);
    if (!old) continue;
    old.replaceWith(document.importNode(fresh, true));
    swapped = true;
  }
  if (!swapped) return false;
  ui.init(host);
  controls.reapply();
  if (hadFocus && !host.contains(document.activeElement)) {
    let target = null;
    if (focusKey) {
      const line = host.querySelector(`[data-line-key="${CSS.escape(focusKey)}"]`);
      if (line) {
        target = focusRole === 'input' ? line.querySelector('input')
          : focusRole === 'remove' ? line.querySelector('[data-line-remove]')
            : focusRole ? line.querySelector(`[data-qty-step="${focusRole.slice(4)}"]`) : null;
        if (target && target.disabled) target = line.querySelector('input');
      }
    }
    if (!target) target = host.querySelector('[data-pp-focus-fallback]');
    if (target) target.focus({ preventScroll: true });
  }
  return true;
}

async function fallbackRender(ids) {
  // a section came back null: render it once more through the Section
  // Rendering API; if that fails too, the canonical state still shows in
  // the counts and a message asks for a refresh
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('sections', ids.slice(0, 5).join(','));
    const res = await fetch(url, { credentials: 'same-origin' });
    if (!res.ok) throw new Error();
    const data = await res.json();
    let all = true;
    for (const id of ids) if (!(data[id] && replaceSection(id, data[id]))) all = false;
    if (!all) throw new Error();
  } catch {
    showCartMessage(strings.cartRefresh, 'refresh');
  }
}

function updateCounts(c) {
  for (const el of document.querySelectorAll('[data-cart-count]')) {
    el.textContent = c.count > 99 ? '99+' : String(c.count);
    // the header pill keeps its count visible and marks the empty state
    if (el.dataset.cartCount === 'keep') el.dataset.empty = String(c.count === 0);
    else el.hidden = c.count === 0;
  }
  // the mini-cart title count "(2 artikelen)": the wording comes from the element
  for (const el of document.querySelectorAll('[data-cart-count-text]')) {
    el.textContent = String((c.count === 1 ? el.dataset.one : el.dataset.many) || '').replace('[count]', c.count);
    el.hidden = c.count === 0;
  }
  const label = c.count === 0 ? strings.cartLabelEmpty : c.count === 1 ? strings.cartLabelOne : strings.cartLabelMany;
  for (const el of document.querySelectorAll('[data-cart-link]')) {
    el.setAttribute('aria-label', (label || '').replace('[count]', c.count));
  }
}

cart.subscribe((e) => {
  // 137 F01: one event-bound message rule for cart and mini-cart; evaluated
  // before a render so a failed refresh of THIS render keeps its warning
  const msg = cartMessageFor(e, cartMessageKind, strings);
  if (msg && msg.clear) showCartMessage('');
  else if (msg) showCartMessage(msg.text, msg.kind);
  if (e.type === 'cart') updateCounts(e.cart);
  if (e.type === 'render') {
    for (const [id, html] of Object.entries(e.sections || {})) if (html) replaceSection(id, html);
    if (e.missing && e.missing.length) fallbackRender(e.missing);
    refreshDynamic();
  }
  if (e.type === 'busy') {
    for (const el of document.querySelectorAll('[data-pp-cart-surface]')) el.setAttribute('aria-busy', e.busy ? 'true' : 'false');
  }
});

/* product and card forms: Ajax add through the engine */
// V07 (r149): an inline loader (aria-busy -> CSS spinner, same button size)
// from the request until the result and the opened mini-cart; the guard is per
// form id and is always released (error, partial, unknown, a throw)
const addGuard = ui.createAddGuard();
const nextFrame = () => new Promise((resolve) => requestAnimationFrame(() => resolve()));
document.addEventListener('submit', async (e) => {
  const form = e.target.closest('form[data-pp-add]');
  if (!form) return;
  e.preventDefault();
  if (!addGuard.begin(form)) return;
  const button = ui.addButtonsFor(form)[0] || null;
  // the button that opened the drawer may have been re-rendered meanwhile
  const opener = () => (button && button.isConnected ? button : ui.addButtonsFor(form)[0] || null);
  try {
    const data = new FormData(form);
    const id = Number(data.get('id'));
    const quantity = Math.max(1, parseInt(data.get('quantity'), 10) || 1);
    const properties = {};
    for (const [k, v] of data.entries()) {
      const m = k.match(/^properties\[(.+)\]$/);
      if (m && v !== '') properties[m[1]] = v;
    }
    const item = { id, quantity, properties };
    if (data.get('selling_plan')) item.selling_plan = Number(data.get('selling_plan'));
    // looked up again after the request: a re-render may have replaced it
    const findError = () => (document.getElementById(form.id) || form).querySelector('[data-pp-form-error]') || document.querySelector(`[data-pp-form-error="${form.id}"]`);
    const errorEl = findError();
    if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }
    let r;
    try {
      r = await cart.add([item], { source: properties._source || 'product_form' });
    } catch {
      // an unexpected throw: the cart state is unknown; show the real cart, never retry
      r = { ok: false, outcome: 'unknown', items: [] };
    }
    if (r.outcome === 'added' || r.outcome === 'partial') {
      track(properties._source === 'pdp_reco' ? 'pdp_upsell_add' : 'pdp_add_to_cart', { variant: id, qty: r.items[0] ? r.items[0].added : quantity, source: properties._source || 'product_form' });
    }
    const liveError = findError();
    if (r.outcome === 'partial' || r.outcome === 'unknown') {
      // something may or may not have been added: show the real cart, never retry silently
      const text = r.outcome === 'partial' ? strings.addPartial : strings.cartUncertain;
      if (liveError) { liveError.textContent = text; liveError.hidden = false; }
      announce(text);
      if (!isCartPage) {
        const drawer = document.getElementById('pp-minicart');
        if (drawer) { ui.openOverlay(drawer, opener()); await nextFrame(); }
      }
    } else if (r.ok) {
      announce(strings.added);
      if (!isCartPage) {
        const drawer = document.getElementById('pp-minicart');
        if (drawer) { ui.openOverlay(drawer, opener()); await nextFrame(); }
      }
    } else {
      const text = (r.error && r.error.description) || strings.addFailed;
      if (liveError) { liveError.textContent = text; liveError.hidden = false; }
      announce(text);
    }
  } finally {
    addGuard.end(form);
  }
});

/* -------------------------------------------------------------- offers */

const storage = (() => { try { return window.sessionStorage; } catch { return null; } })();
const dismissals = createDismissals(storage || { getItem: () => null, setItem: () => {} });

function cartWorld() {
  const d = cartData();
  return { world: worldFromData(d, config.ladder || {}, config.cartPowerExceptions || []), cartPackComponents: d.cartPackComponents || [] };
}

function refreshReco() {
  for (const section of document.querySelectorAll('[data-pp-reco]')) {
    const data = readJson(section.querySelector('[data-pp-offers]'));
    if (!data) continue;
    const lines = ((cart.view().cart || {}).lines || []).map((l) => ({ product: String(l.product) }));
    const world = worldFromData(data, config.ladder || {}, config.cartPowerExceptions || []);
    const result = evaluate(world, {
      surface: 'pdp',
      currentProduct: String(data.currentProduct),
      currentPackComponents: (data.currentPackComponents || []).map(String),
      cartLines: lines
    });
    const list = section.querySelector('[data-reco-list]');
    const cards = new Map([...section.querySelectorAll('[data-offer-handle]')].map((c) => [c.dataset.offerHandle, c]));
    for (const c of cards.values()) c.hidden = true;
    for (const o of result.pdp) {
      const card = cards.get(o.handle);
      if (!card) continue;
      card.hidden = false;
      list.appendChild(card);                // priority order, moves only
      if (!card.dataset.viewed) { card.dataset.viewed = '1'; track('pdp_upsell_view', { offer: o.handle, product: o.product }); }
    }
    section.hidden = result.pdp.length === 0;
  }
}

/* -------------------------------------------------------- checkout step */

const checkoutDialog = document.getElementById('pp-checkout-flow');
let checkout = null;
if (checkoutDialog) {
  const view = createDialogView(checkoutDialog, {
    money,
    strings: strings.checkout || {},
    onMessage: showCartMessage,
    onClose: ({ origin, opener }) => {
      if (origin === 'minicart') ui.openOverlay(document.getElementById('pp-minicart'), opener);
      else if (opener && opener.isConnected) opener.focus();
    }
  });
  const open = view.open;
  view.open = (ctx) => {
    const cur = ui.currentOverlay();
    if (cur) ui.closeOverlay(cur.dialog, { restore: false });   // swap layers, never stack
    open(ctx);
  };
  checkout = createCheckout({
    cart,
    evaluate,
    readWorld: cartWorld,
    dismissals,
    track,
    view,
    submit: () => {
      const form = document.getElementById('pp-checkout-form');
      if (form) form.submit();
    }
  });
  checkoutDialog.addEventListener('pp:overlay-cancel', () => checkout.act('cancel'));
  checkoutDialog.addEventListener('click', (e) => {
    const b = e.target.closest('[data-cf]');
    if (!b || b.disabled) return;
    if (b.dataset.cf === 'qty') {
      const input = checkoutDialog.querySelector(`[data-cf-qty="${CSS.escape(b.dataset.handle)}"]`);
      checkout.act('qty', b.dataset.handle, (Number(input && input.value) || 1) + Number(b.dataset.step));
      return;
    }
    checkout.act(b.dataset.cf, b.dataset.handle);
  });
  checkoutDialog.addEventListener('change', (e) => {
    const input = e.target.closest('[data-cf-qty]');
    if (input) checkout.act('qty', input.dataset.cfQty, input.value);
  });
}

/* cart surfaces and the two checkout entries: one set of handlers */
const controls = createCartControls({
  root: document,
  cart,
  getCheckout: () => checkout,
  strings,
  announce,
  showMessage: showCartMessage
});
document.addEventListener('input', controls.onInput);
document.addEventListener('change', controls.onChange);
document.addEventListener('click', controls.onClick);
document.addEventListener('submit', controls.onSubmit);
// back/forward cache: a page left for checkout must not come back locked
window.addEventListener('pageshow', (e) => { if (e.persisted) controls.unlock(); });

/* ------------------------------------------------------------ delivery */

const calendar = readJson(document.getElementById('pp-delivery-calendar')) || [];
let tick = null;
function refreshDelivery() {
  clearTimeout(tick);
  let ticking = false;
  try {
    ticking = renderDelivery(document, { calendar, strings: strings.delivery || {}, locale: config.locale || 'nl-NL' });
  } catch { /* the calm Liquid status stays */ }
  if (document.hidden) return;
  const ms = Date.now() % 1000;
  tick = setTimeout(refreshDelivery, ticking ? 1000 - ms + 20 : 60000);
}
document.addEventListener('visibilitychange', () => { if (!document.hidden) refreshDelivery(); });

/* --------------------------------------------- family modules (lazy) */
// Loaded only on pages that contain their section; each root is wired once
// and disposed when the Theme Editor unloads its section.

const familyDisposers = new Map();
function wireFamily(selector, specifier, initName, root = document) {
  const roots = [...root.querySelectorAll(selector)].filter((el) => !familyDisposers.has(el));
  if (!roots.length) return;
  import(specifier).then((mod) => {
    for (const el of roots) {
      if (familyDisposers.has(el) || !el.isConnected) continue;
      try { familyDisposers.set(el, mod[initName](el, { locale: config.locale || 'nl-NL' })); } catch { /* the server-rendered state stays */ }
    }
  }).catch(() => {});
}
function wireFamilies(root = document) {
  wireFamily('[data-food-calc]', '@pp/food', 'initFoodCalc', root);
  wireFamily('[data-power-helper]', '@pp/power', 'initPowerHelper', root);
}
document.addEventListener('shopify:section:unload', (e) => {
  for (const [el, dispose] of familyDisposers) {
    if (e.target.contains(el)) { if (typeof dispose === 'function') dispose(); familyDisposers.delete(el); }
  }
});

/* ------------------------------------------------- scan navigation counts */
// Noodspullen: each scan link shows the rendered, de-duplicated product count
// of the group it points at (the group section's data-group-count); one
// source, no second product list. Without a count the line stays hidden.
export function catnavLabel(n, one, many) {
  if (!Number.isFinite(n) || n < 1) return '';
  return String(n === 1 ? one : many).replace('[count]', String(n));
}

function refreshCatnavCounts(root = document) {
  for (const nav of root.querySelectorAll('[data-catnav]')) {
    for (const a of nav.querySelectorAll('a[href^="#"]')) {
      const out = a.querySelector('[data-catnav-count]');
      if (!out) continue;
      const group = document.getElementById(decodeURIComponent(a.getAttribute('href').slice(1)));
      const label = group ? catnavLabel(Number(group.dataset.groupCount), nav.dataset.countOne, nav.dataset.countMany) : '';
      out.textContent = label;
      out.hidden = label === '';
    }
  }
}

/* ---------------------------------------------------------------- boot */

function refreshDynamic() {
  refreshReco();
  refreshDelivery();
  wireFamilies();
  refreshCatnavCounts();
}

ui.start({ isCartPage });
refreshDynamic();
if (isCartPage) track('cart_view', {});
document.addEventListener('shopify:section:load', refreshDynamic);
